"use client"

import { useDispatch, useSelector } from "react-redux"
import moment from "moment"
import { FalseIcon, TrueIcon } from "../../../utils/commonIcons"
import { ModalComponent, ReusableDataTable } from '@operations/shared';
import { useState } from "react"
import { Typography, Descriptions, Alert, Space, Tag, message } from "antd"
import { ExclamationCircleOutlined } from "@ant-design/icons"
import { patchApi } from '../../../../../shared/src/apis'
import { stepperMoving } from '../../../stores/actions'

const { Title, Text } = Typography

export default function ViewFreePeriodRequests() {
    const { id } = useSelector((state: any) => state.actionsReducer?.info)
    const [requestData, setRequestData] = useState<any>([])
    const [visibleModal, setVisibleModal] = useState(false)
    const [loading, setLoading] = useState(false)
    const dispatch = useDispatch();

    const handleSuspend = (record: any) => {
        setRequestData(record)
        setVisibleModal(true)
    }

    const columns = [
        {
            title: "Suspend action",
            key: "suspend",
            fixed: "left",
            render: (_: any, record: any) => (
                <button
                    onClick={() => !record?.isSuspended && handleSuspend(record)}
                    disabled={record?.isSuspended || !record?.isActive}
                    style={{
                        backgroundColor: "#ff4d4f",
                        color: "#fff",
                        border: "none",
                        borderRadius: "4px",
                        padding: "6px 12px",
                        cursor: record?.isSuspended || !record?.isActive ? "not-allowed" : "pointer",
                        fontWeight: 500,
                        opacity: record?.isSuspended || !record?.isActive ? 0.5 : 1,
                    }}
                >
                    Suspend
                </button>
            ),
        },
        {
            title: "Free Period Request ID",
            dataIndex: "id",
            key: "id",
        },
        {
            title: "isActive",
            dataIndex: "isActive",
            key: "isActive",
            render: (isActive: boolean) => (isActive ? TrueIcon : FalseIcon),
        },
        {
            title: "isSuspended",
            dataIndex: "isSuspended",
            key: "isSuspended",
            render: (isSuspended: boolean) => (isSuspended ? TrueIcon : FalseIcon),
        },
        {
            title: "Company ID",
            dataIndex: "companyID",
            key: "companyID",
        },
        {
            title: "Free Period Type",
            dataIndex: "freePeriodTypeID",
            key: "freePeriodTypeID",
            render: (freePeriodTypeID: number) => {
                if (freePeriodTypeID === 2) {
                    return <Tag color="blue">Free Months Mode</Tag>;
                }
                if (freePeriodTypeID === 1) {
                    return <Tag color="purple">Discount Mode</Tag>;
                }
                return "N/A";
            },
        },
        {
            title: "Discount Percent",
            dataIndex: "discountPercent",
            key: "discountPercent",
            render: (discountPercent: number) => {
                if (discountPercent == null) return "N/A";
                return `${Math.floor(discountPercent)}%`;
            },
        },
        {
            title: "Number Of Free Months",
            dataIndex: "numberOfFreeMonths",
            key: "numberOfFreeMonths",
            render: (numberOfFreeMonths: number) => {
                if (numberOfFreeMonths == null) return "N/A";
                return numberOfFreeMonths;
            },
        },
        {
            title: "Min Number Of Free Installments",
            dataIndex: "minNumberOfFreeInstallments",
            key: "minNumberOfFreeInstallments",
            render: (minNumberOfFreeInstallments: number) => {
                if (minNumberOfFreeInstallments == null) return "N/A";
                return minNumberOfFreeInstallments;
            },
        },
        {
            title: "Max Amount Per User",
            dataIndex: "maxAmountPerUser",
            key: "maxAmountPerUser",
        },
        {
            title: "Max Number Of Requests Per User",
            dataIndex: "maxNumberOfRequestsPerUser",
            key: "maxNumberOfRequestsPerUser",
        },
        {
            title: "Service Group ID",
            dataIndex: "serviceGroupID",
            key: "serviceGroupID",
        },
        {
            title: "Service Group Name",
            dataIndex: "serviceGroupName",
            key: "serviceGroupName",
        },
        {
            title: "First Transaction User",
            dataIndex: "firstTransactionUser",
            key: "firstTransactionUser",
            render: (firstTransactionUser: boolean) => (firstTransactionUser ? TrueIcon : FalseIcon),
        },
        {
            title: "Created By",
            dataIndex: "createdBy",
            key: "createdBy",
        },
        {
            title: "Start Date",
            dataIndex: "startDate",
            key: "startDate",
            render: (date: string) => moment(date).format("LL"),
        },
        {
            title: "End Date",
            dataIndex: "endDate",
            key: "endDate",
            render: (date: string) => moment(date).format("LL"),
        },
        {
            title: "Created At",
            dataIndex: "createdAt",
            key: "createdAt",
            render: (date: string) => moment(date).format("LLL"),
        },
        
        {
            title: "Company Name",
            dataIndex: "companyName",
            key: "companyName",
        },
    ]

    const handleCloseTheModal = () => {
        setVisibleModal(false)
    }

    const handleAcceptModal = async () => {
        setLoading(true)

        if (!requestData?.id) return;
        try {
            const res = await patchApi(`/companies/suspendFreePeriod?id=${requestData?.id}`, {})(() => { });
            if (res?.payload?.status === 200) {
                setVisibleModal(false);
                message.success("Free period request suspended successfully");
                dispatch(stepperMoving(0))
            } else {
                message.error("Failed to suspend free period request")
            }
        } catch (error) {
            // Optionally, handle error (show notification, etc.)
            console.error(error);
        }
        setLoading(false)

    }

    const suspendMessageContent = (
        <Space direction="vertical" size="large" style={{ width: "100%" }}>
            <div style={{ textAlign: "center" }}>
                <ExclamationCircleOutlined
                    style={{
                        fontSize: 48,
                        color: "#faad14",
                        marginBottom: 16,
                    }}
                />
                <Title level={4} style={{ margin: 0, color: "#262626" }}>
                    Suspend Free Period Request
                </Title>
                <Text type="secondary">You are about to suspend the following free period request</Text>
            </div>

            {/* Request Details */}
            <Descriptions
                title="Request Details"
                bordered
                column={1}
                size="small"
                labelStyle={{
                    fontWeight: 600,
                    backgroundColor: "#fafafa",
                    width: "40%",
                }}
                contentStyle={{
                    backgroundColor: "#fff",
                    fontWeight: 500,
                }}
            >
                <Descriptions.Item label="Request ID">
                    <Tag color="blue">{requestData?.id}</Tag>
                </Descriptions.Item>

                <Descriptions.Item label="Company Name">
                    <Text strong>{requestData?.companyName}</Text>
                </Descriptions.Item>

                <Descriptions.Item label="Service Group">
                    <Text>{requestData?.serviceGroupName}</Text>
                </Descriptions.Item>

                <Descriptions.Item label="Free Period Type">
                    <Tag color={requestData?.freePeriodTypeID === 2 ? "blue" : "purple"}>
                        {requestData?.freePeriodTypeID === 2 ? "Free Months Mode" : requestData?.freePeriodTypeID === 1 ? "Discount Mode" : "N/A"}
                    </Tag>
                </Descriptions.Item>

                {requestData?.freePeriodTypeID === 1 && (
                    <Descriptions.Item label="Free Discount">
                        <Tag color="green">{Math.floor(requestData?.discountPercent || 0)} %</Tag>
                    </Descriptions.Item>
                )}

                {requestData?.freePeriodTypeID === 2 && (
                    <>
                        <Descriptions.Item label="Number Of Free Months">
                            <Text code>{requestData?.numberOfFreeMonths ?? "N/A"}</Text>
                        </Descriptions.Item>

                        <Descriptions.Item label="Min Number Of Free Installments">
                            <Text code>{requestData?.minNumberOfFreeInstallments ?? "N/A"}</Text>
                        </Descriptions.Item>
                    </>
                )}

                <Descriptions.Item label="Max Amount Per User">
                    <Text code>{requestData?.maxAmountPerUser}</Text>
                </Descriptions.Item>

                <Descriptions.Item label="Max Requests Per User">
                    <Text code>{requestData?.maxNumberOfRequestsPerUser}</Text>
                </Descriptions.Item>

                <Descriptions.Item label="Start Date">
                    <Text>{requestData?.startDate ? moment(requestData?.startDate).format("LL") : "N/A"}</Text>
                </Descriptions.Item>

                <Descriptions.Item label="End Date">
                    <Text>{requestData?.endDate ? moment(requestData?.endDate).format("LL") : "N/A"}</Text>
                </Descriptions.Item>
            </Descriptions>

            {/* Warning Alert */}
            <Alert
                message="Warning"
                description="Are you sure you want to suspend this request? This action cannot be undone and will immediately deactivate the free period benefits for all associated users."
                type="warning"
                showIcon
                icon={<ExclamationCircleOutlined />}
                style={{
                    borderRadius: 8,
                    border: "1px solid #faad14",
                }}
            />
        </Space>
    )

    return (
        <>
            <div
                style={{ display: "flex", flexDirection: "column", marginTop: "20px", overflowY: "auto" }}
            >
                <ReusableDataTable
                    columns={columns}
                    hiddenRefreshing
                    apiLink={`/companies/freePeriod?companyID=${id}`}
                    pagination={false}
                    loading={false}
                    scrolling={{ x: "max-content" }}
                    loadData={true}
                />
            </div>
            <ModalComponent
                content={suspendMessageContent}
                title={`Suspend Free Period Request`}
                visibleModal={visibleModal}
                okText='Suspend'
                handleCloseModal={handleCloseTheModal}
                handleAcceptModal={handleAcceptModal}
                btnDisable={loading}
            />
        </>
    )
}
